import axios from 'axios'
import { doLogout } from './auth'

interface RequestConfig {
    useAuth?: boolean
    multipart?: boolean
    useRecaptcha?: boolean
}

const defaultRequestConfig: RequestConfig = {
    useAuth: true,
    multipart: false,
    useRecaptcha: false,
}

export class HttpError extends Error {
    status: number
    detail?: string

    constructor(message: string, status: number, detail?: string) {
        super(message)
        this.status = status
        this.detail = detail
    }
}

export const GLOBAL_REQUEST_CONFIGS = {
    rows: 10
}

function getRequesterInfo() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
  };
}


function getInstance(requestConfig?: RequestConfig) {
    const config = requestConfig
        ? { ...defaultRequestConfig, ...requestConfig }
        : { ...defaultRequestConfig }

    const baseURL =
        process.env.REACT_APP_MODE === 'cloud'
            ? process.env.REACT_APP_API_ENDPOINT
            : `http://${window.location.hostname}:4000/api`

    const token = localStorage.getItem('token')

    if (config?.useAuth && !token) {
        throw new Error('User not authenticated!')
    }

    const headers = {
        'Content-Type': !config.multipart
            ? 'application/json'
            : 'requestConfig/form-data',
        Authorization: config?.useAuth ? `${token}` : undefined,
        hash_caixa: localStorage.getItem('cashierHash') ?? undefined,
    }

    const instance = axios.create({
        baseURL,
        headers,
    })

    instance.interceptors.request.use(
        async (requestConfig) => {
            if (config.useRecaptcha && process.env.REACT_APP_MODE === 'cloud') {
                const recaptchaToken = await execupteRecaptcha(
                    `${requestConfig.method}_${requestConfig.url}`
                )

                requestConfig.headers['recaptcha-token'] = recaptchaToken
            }

            if (requestConfig.data && typeof requestConfig.data === 'object') {
                requestConfig.data.requesterInfo = getRequesterInfo();
                if(typeof requestConfig.data.rows === 'number')
                    requestConfig.data.rows = GLOBAL_REQUEST_CONFIGS.rows
                if(typeof requestConfig.data.take === 'number')
                    requestConfig.data.take = GLOBAL_REQUEST_CONFIGS.rows
            }

            if(requestConfig.url) {
                const url = new URL(requestConfig.url, requestConfig.baseURL);

                if (url.searchParams.has("rows")) {
                    url.searchParams.set("rows", GLOBAL_REQUEST_CONFIGS.rows.toString());
                }

                if (url.searchParams.has("take")) {
                    url.searchParams.set("take", GLOBAL_REQUEST_CONFIGS.rows.toString());
                }

                requestConfig.url = url.pathname + url.search;
            }
            
            return requestConfig
        },
        (err) => Promise.reject(err)
    )

    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (
                error.response?.data?.data?.message ===
                'Unauthorized, token expired'
            ) {
                doLogout()
                window.location.href = '/entrar?expired=true'
                return
            }

            throw new HttpError(
                error.response?.data?.data?.message,
                error.response?.status,
                error.response?.data?.data?.stack
            )
        }
    )

    return instance
}

export async function get(endpoint: string, requestConfig?: RequestConfig) {
    const axios = getInstance(requestConfig)
    const res = await axios.get(endpoint)
    return res
}

export async function post(
    endpoint: string,
    body: any,
    requestConfig?: RequestConfig
) {
    const axios = getInstance(requestConfig)
    const res = await axios.post(endpoint, body)
    return res
}

export async function put(
    endpoint: string,
    body: any,
    requestConfig?: RequestConfig
) {
    const axios = getInstance(requestConfig)
    const res = await axios.put(endpoint, body)
    return res
}

export async function del(
    endpoint: string,
    body?: any,
    requestConfig?: RequestConfig
) {
    const axios = getInstance(requestConfig)
    const res = await axios.delete(endpoint, { data: body })
    return res
}

export function execupteRecaptcha(action?: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const key = process.env.REACT_APP_RECAPTCHA_SITE_KEY

        if (
            !window.grecaptcha ||
            !key ||
            process.env.REACT_APP_MODE !== 'cloud'
        ) {
            reject()
            return
        }

        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(key, { action: /* action ?? */ 'submit' })
                .then(
                    (token) => resolve(token),
                    (err) => reject(err)
                )
        })
    })
}

export function mountQuery(data: any) {
    let query = ''
    for (const key of Object.keys(data)) {
        if (data[key] === undefined) continue
        query += `${key}=${data[key]}&`
    }

    return query
}